import React from 'react';
import { Cpu, KeyRound, Pencil, Plus, Power, PowerOff } from 'lucide-react';

import {
  BottomSheet,
  CardAction,
  ChipRow,
  EmptyState,
  PageHeader,
  PhonePage,
  PillButton,
  PrimaryButton,
  ScreenLoader,
  SearchBar,
  StatusPill,
} from '@/components/phone/primitives';
import { INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';

/**
 * The plant's machines, on a phone.
 *
 * A card per press says what it is, which stages it runs, how much is queued
 * on it and whether it is switched on — the two things the desktop list was
 * rebuilt to show. The stage filter opens as a sheet and the inactive toggle
 * is a chip, so the same narrowing is a thumb away.
 */

function MachineCard({ machine: m, stageName, onEdit, onReset, onToggle }) {
  const active = m.status !== 'inactive';
  const names = m.linked_stage_names?.length ? m.linked_stage_names : (m.linked_stage_ids || []).map(stageName);

  return (
    <li
      data-testid={`machine-card-${m.id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <div className="px-4 pt-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>{m.machine_name}</p>
            <p className="truncate text-[12px]" style={{ color: Colors.text.tertiary }}>{m.username || '—'}</p>
          </div>
          <StatusPill label={active ? 'active' : 'inactive'} tone={active ? 'positive' : 'neutral'} dot />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {names.length === 0 && (
            <span className="text-[12px]" style={{ color: Colors.text.tertiary }}>Not linked to a stage</span>
          )}
          {names.slice(0, 4).map((s, i) => <StatusPill key={`${s}-${i}`} label={s} tone="accent" />)}
          {names.length > 4 && (
            <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>+{names.length - 4}</span>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 py-3 text-[12px]">
          <span className="truncate" style={{ color: Colors.text.secondary }}>{m.notes || 'No notes'}</span>
          <span
            className="shrink-0 font-semibold tabular-nums"
            style={{ color: m.queue_count ? Colors.accent.DEFAULT : Colors.text.tertiary }}
            data-testid={`machine-queue-${m.id}`}
          >
            {m.queue_count || 0} queued
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto border-t px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ borderColor: Colors.border.subtle }}>
        <CardAction icon={Pencil} label="Edit machine" onClick={onEdit} testID={`edit-machine-${m.id}`} />
        <CardAction icon={KeyRound} label="Reset password" onClick={onReset} testID={`reset-machine-${m.id}`} />
        <CardAction
          icon={active ? PowerOff : Power}
          label={active ? 'Deactivate machine' : 'Activate machine'}
          tone={active ? 'critical' : 'positive'}
          onClick={onToggle}
          testID={`toggle-machine-${m.id}`}
        />
      </div>
    </li>
  );
}

function ResetPasswordSheet({ machine, password, setPassword, onSubmit, onClose }) {
  return (
    <BottomSheet
      open={!!machine}
      onClose={onClose}
      title={`Reset password — ${machine?.machine_name || ''}`}
      subtitle="The operator will need the new password on their next sign-in"
      testID="reset-password-sheet"
    >
      <p className="mb-1.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>New password</p>
      <input
        data-testid="reset-password-input"
        className={INPUT_CLASS}
        style={inputStyle}
        type="text"
        value={password}
        placeholder="At least 6 characters"
        autoCapitalize="none"
        onChange={(e) => setPassword(e.target.value)}
      />
      <PrimaryButton className="mt-4" onClick={onSubmit} testID="reset-password-submit">Reset password</PrimaryButton>
    </BottomSheet>
  );
}

export default function PhoneMachinesList({
  machines, total, loading, onSearch, allStages, stageFilter, onStageFilter, showInactive, onShowInactive,
  stageName, onNew, onEdit, onReset, onToggle,
  resetFor, newPassword, setNewPassword, onResetSubmit, onResetClose,
}) {
  const stages = [
    { value: 'all', label: 'All stages' },
    ...allStages.map((s) => ({ value: s.stage_name, label: s.stage_name })),
  ];

  return (
    <PhonePage testID="machines-page">
      <PageHeader
        title="Machines"
        subtitle={`${total} on the floor`}
        action={(
          <PillButton onClick={onNew} testID="add-machine-btn">
            <Plus className="h-4 w-4" /> New
          </PillButton>
        )}
      />

      <div className="px-5">
        <SearchBar placeholder="Machine, username, stage…" onChange={onSearch} testID="machines-search-input" />
        <div className="mt-3">
          <SelectField name="stage" label="Runs which stage" value={stageFilter} options={stages} onChange={onStageFilter} />
        </div>
        <div className="mt-2">
          <ChipRow
            chips={[{ key: 'active', label: 'Active only' }, { key: 'all', label: 'Include inactive' }]}
            active={showInactive ? 'all' : 'active'}
            onSelect={(key) => onShowInactive(key === 'all')}
            testIDPrefix="chip"
          />
        </div>
      </div>

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : machines.length === 0 ? (
          <EmptyState icon={Cpu} message="No machines found" />
        ) : (
          <ul data-testid="machines-list">
            {machines.map((m) => (
              <MachineCard
                key={m.id}
                machine={m}
                stageName={stageName}
                onEdit={() => onEdit(m)}
                onReset={() => onReset(m)}
                onToggle={() => onToggle(m)}
              />
            ))}
          </ul>
        )}
      </div>

      <ResetPasswordSheet
        machine={resetFor}
        password={newPassword}
        setPassword={setNewPassword}
        onSubmit={onResetSubmit}
        onClose={onResetClose}
      />
    </PhonePage>
  );
}
